import {
  bool,
  number,
  string,
  oneOf,
  func,
  node,
  array,
  object,
  oneOfType,
} from 'prop-types'
import { Component } from 'react'
import { AnalyticsConsumer } from '@opallabs/quanta'
import { kuiperClassNames, noPropagate } from '../utils'
import ButtonLabel from './ButtonLabel'

import './styles.scss'

export default class UnwrappedButton extends Component {
  static propTypes = {
    'aria-hidden': bool,
    ariaLabel: string,
    className: string,
    color: oneOf([
      'white',
      'red',
      'orange',
      'yellow',
      'green',
      'seafoam',
      'blue',
    ]),
    disabled: bool,
    focused: bool,
    hasMenu: bool,
    hideLabelText: bool,
    icon: string,
    iconOptions: object,
    isLoading: bool,
    label: oneOfType([string, node, array]),
    menuOptions: object,
    name: string,
    noArrow: bool,
    onBlur: func,
    onClick: func,
    onFocus: func,
    onKeyDown: func,
    onMouseDown: func,
    onSelect: func,
    position: oneOf(['left', 'right']),
    resizeHandle: node,
    size: oneOf(['large', 'medium', 'small', 'wide']),
    style: object,
    tabIndex: number,
    theme: string,
    trackElement: oneOfType([func, object]),
    trackingLabel: string,
    type: oneOf([
      'primary',
      'secondary',
      'nobutton',
      'invisible',
      'invisible_light',
    ]),
    typeAttribute: oneOf(['submit', 'button']),
  }

  static defaultProps = {
    disabled: false,
    hideLabelText: false,
    isLoading: false,
    noArrow: false,
    onFocus: () => {},
    onKeyDown: () => {},
    position: 'left',
    size: 'large',
    style: {},
    tabIndex: 0,
    type: 'primary',
    typeAttribute: 'submit',
  }

  constructor(props) {
    super(props)

    this.menuItemClick = noPropagate(this.menuItemClick.bind(this))
    this.onClick = noPropagate(this.onClick.bind(this))
    this.onMouseDown = noPropagate(this.onMouseDown.bind(this))
  }

  get kuiperClassNames() {
    return kuiperClassNames('button', {
      disabled: this.props.disabled,
      icon: this.props.icon,
      [this.props.className]: !!this.props.className,
      'has-label': !!this.props.label,
      'has-menu': this.hasMenu,
      'has-main-click': this.hasMainClick,
      [this.props.type]: this.props.type,
      invisible: this.props.type === 'invisible',
      [`icon-${this.props.icon}`]: !!this.props.icon,
      [`icon-position-${this.props.position}`]: this.props.icon,
      [`color-${this.color}`]:
        !!this.color &&
        ['primary', 'invisible', 'invisible_light'].includes(this.props.type),
      [`size-${this.props.size}`]: this.props.size,
      open: this.props.focused,
      'menu-button': this.hasMenu,
    })
  }

  get hasMainClick() {
    return Boolean(this.props.onClick || this.props.onMouseDown)
  }

  get hasMenu() {
    return this.props.hasMenu
  }

  get color() {
    if (this.props.type === 'primary' || this.props.type === 'link') {
      return this.props.color || 'blue'
    }

    return this.props.color
  }

  menuItemClick(item) {
    if (this.props.disabled) {
      return false
    }
    if (this.props.onSelect) {
      this.props.onSelect(item)
    }
    this.onBlur()
    return true
  }

  onClick(e, track) {
    if (this.props.disabled) return false

    track({
      value:
        this.props.trackingLabel ||
        this.props.label ||
        this.props.ariaLabel ||
        '[Button Has No Label]',
    })

    if (e && this.props.onClick) {
      return this.props.onClick(e)
    }

    if (this.props.focused) {
      return this.props.onBlur(e)
    }

    return this.props.onFocus(e)
  }

  onMouseDown(e, track) {
    if (this.props.disabled) return false

    track({
      value:
        this.props.trackingLabel ||
        this.props.label ||
        this.props.ariaLabel ||
        '[Button Has No Label]',
    })

    if (e && this.props.onMouseDown) {
      return this.props.onMouseDown(e)
    }

    if (this.props.focused) {
      return this.props.onBlur(e)
    }

    return this.props.onFocus(e)
  }

  get label() {
    const {
      disabled,
      hideLabelText,
      icon,
      label,
      isLoading,
      noArrow,
      position,
      onFocus,
      onBlur,
      focused,
      iconOptions,
      menuOptions,
      theme,
    } = this.props
    return (
      <ButtonLabel
        disabled={disabled}
        focused={focused}
        hasMenu={this.hasMenu}
        hideLabelText={hideLabelText}
        icon={icon}
        iconOptions={iconOptions}
        isLoading={isLoading}
        label={label}
        noArrow={noArrow}
        onBlur={onBlur}
        onFocus={onFocus}
        position={position}
        theme={theme}
        {...menuOptions}
      />
    )
  }

  render() {
    const {
      ariaLabel,
      disabled,
      name,
      onBlur,
      onFocus,
      onKeyDown,
      resizeHandle,
      style,
      tabIndex,
      trackElement,
      typeAttribute,
    } = this.props
    return (
      <AnalyticsConsumer>
        {(tracker) => (
          <button
            aria-label={ariaLabel}
            aria-hidden={this.props['aria-hidden']}
            name={name}
            onFocus={onFocus}
            onBlur={onBlur}
            onClick={(e) => {
              this.onClick(e, tracker.click)
            }}
            onMouseDown={(e) => {
              this.onMouseDown(e, tracker.click)
            }}
            onKeyDown={onKeyDown}
            tabIndex={tabIndex}
            type={typeAttribute}
            ref={trackElement}
            style={style}
            className={this.kuiperClassNames}
            disabled={disabled}
          >
            {this.label}
            {resizeHandle}
          </button>
        )}
      </AnalyticsConsumer>
    )
  }
}
